import { useTranslation } from 'react-i18next'
import { sortByProperty } from 'helpers'

import { useSiteStore } from 'js/lib/stores/SiteStore'

const useSubtypes = (
    requestedTypes: string[] = [],
    {
        includeCustomTypes = false,
    }: {
        includeCustomTypes?: boolean
    } = {},
) => {
    const { t } = useTranslation()
    const {
        site: { isCustomContentTypeEnabled, customContentTypes },
    } = useSiteStore()

    const defaultTypes = [
        {
            key: 'blog',
            name: t(`entity-blog.content-name`),
            slug: 'blog',
        },
        {
            key: 'discussion',
            name: t(`entity-discussion.content-name`),
            slug: 'discussion',
        },
        {
            key: 'episode',
            name: t(`entity-episode.content-name`),
            slug: 'episodes',
        },
        {
            key: 'event',
            name: t(`entity-event.content-name`),
            slug: 'events',
        },
        {
            key: 'file',
            name: t(`entity-file.content-name`),
            slug: 'files',
        },
        {
            key: 'folder',
            name: t(`entity-folder.content-name`),
            slug: 'folders',
        },
        {
            key: 'news',
            name: t(`entity-news.content-name`),
            slug: 'news',
        },
        {
            key: 'pad',
            name: t(`entity-pad.content-name`),
            slug: 'pads',
        },
        {
            key: 'page-text',
            name: t(`entity-page-text.content-name`),
            slug: 'pages',
        },
        {
            key: 'page-widget',
            name: t(`entity-cms.content-name`),
            slug: 'pages',
        },
        {
            key: 'podcast',
            name: t(`entity-podcast.content-name`),
            slug: 'podcasts',
        },
        {
            key: 'question',
            name: t(`entity-question.content-name`),
            slug: 'questions',
        },
        {
            key: 'statusupdate',
            name: t(`entity-statusupdate.content-name`),
            slug: 'statusupdates',
        },
        {
            key: 'task',
            name: t(`entity-task.content-name`),
            slug: 'tasks',
        },
        {
            key: 'wiki',
            name: t(`entity-wiki.content-name`),
            slug: 'wiki',
        },
        {
            key: 'magazine',
            name: t('entity-magazine.name_one'),
            slug: 'magazines',
        },
        {
            key: 'magazine_issue',
            name: t('entity-magazine.issue-content-name'),
            slug: 'magazine-issue',
        },
        {
            key: 'image',
            name: t(`global.image`),
            slug: 'files',
        },
        {
            key: 'group',
            name: t(`entity-group.content-name`),
            slug: 'groups',
        },
        {
            key: 'page',
            name: t(`entity-cms.content-name`),
            slug: 'pages',
        },
        {
            key: 'user',
            name: t(`admin.user`),
            slug: '',
        },
        {
            key: 'poll',
            name: t(`entity-poll.content-name`),
            slug: 'polls',
        },
    ]

    const customTypes = isCustomContentTypeEnabled
        ? customContentTypes.map(({ key, name, slug }) => ({
              key,
              name,
              slug,
          }))
        : []

    if (requestedTypes.length > 0) {
        return [
            ...defaultTypes.filter(({ key }) => requestedTypes.includes(key)),
            ...(includeCustomTypes ? customTypes : []),
        ]
    } else {
        // Return all types if no types are requested
        return [...defaultTypes, ...customTypes]
    }
}

export const useSubtypeOptions = (
    requestedTypes: string[] = [],
    {
        includeCustomTypes = false,
    }: {
        includeCustomTypes?: boolean
    } = {},
) => {
    const subtypes = useSubtypes(requestedTypes, { includeCustomTypes })

    const subtypeOptions = subtypes
        .map(({ key, name }) => ({
            value: key,
            label: name,
        }))
        .sort(sortByProperty('label'))

    return subtypeOptions
}

export const useSubtypeKeys = (
    requestedTypes: string[] = [],
    {
        includeCustomTypes = false,
    }: {
        includeCustomTypes?: boolean
    } = {},
) => {
    const subtypes = useSubtypes(requestedTypes, { includeCustomTypes })
    const subtypeKeys = subtypes.map(({ key }) => key)

    return subtypeKeys
}

export const useSubtype = (requestedType: string = '') => {
    const subtypes = useSubtypes()
    const subtype = subtypes.find(({ key }) => key === requestedType)

    return subtype || {}
}

export default useSubtypes
